import cn from 'classnames'
import { FC, useRef } from 'react'
import { Swiper as SwiperType } from 'swiper'
import { Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { PaginationOptions } from 'swiper/types'

import { DiamondAnimation, InventoryThing } from '@shared'
import { InventoryCard } from '@store'

import { splitArray } from '../../utils'
import { SpiralSelectorArrowIcon } from '../SpiralSelector/icons'
import styles from './CollectedItems.module.scss'

type Props = {
  items: InventoryCard[]
}

export const CollectedItems: FC<Props> = ({ items }) => {
  const swiperRef = useRef<SwiperType | null>(null)
  const pagination: PaginationOptions = {
    clickable: true,
    renderBullet: (_, className) => {
      return `<span class="${styles.paginationBullet} ${className}"></span>`
    },
  }

  const pagedItems = splitArray<InventoryCard>(items, 4)

  return (
    <div className={styles.container}>
      <svg
        className={styles.border}
        viewBox="0 0 375 577"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M32 38.5H343C351.56 38.5 358.5 45.4396 358.5 54V378C358.5 386.56 351.56 393.5 343 393.5H304.502C299.26 393.5 294.341 395.99 291.236 400.188L290.941 400.6L277.611 419.83C274.716 424.007 269.956 426.5 264.873 426.5H110.127C105.203 426.5 100.582 424.161 97.665 420.217L97.3887 419.83L84.0586 400.6C80.976 396.153 75.9088 393.5 70.498 393.5H32C23.4396 393.5 16.5 386.56 16.5 378V54C16.5 45.4396 23.4396 38.5 32 38.5Z"
          stroke="url(#paint1_linear_1154_9948)"
        />
        <path
          d="M32 38.5H343C351.56 38.5 358.5 45.4396 358.5 54V378C358.5 386.56 351.56 393.5 343 393.5H304.502C299.26 393.5 294.341 395.99 291.236 400.188L290.941 400.6L277.611 419.83C274.716 424.007 269.956 426.5 264.873 426.5H110.127C105.203 426.5 100.582 424.161 97.665 420.217L97.3887 419.83L84.0586 400.6C80.976 396.153 75.9088 393.5 70.498 393.5H32C23.4396 393.5 16.5 386.56 16.5 378V54C16.5 45.4396 23.4396 38.5 32 38.5Z"
          stroke="url(#paint2_linear_1154_9948)"
        />
        <defs>
          <linearGradient
            id="paint1_linear_1154_9948"
            x1="415.5"
            y1="490.271"
            x2="-21.5976"
            y2="374.714"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#000" />
            <stop offset="1" stopColor="#7F7F7F" />
          </linearGradient>
          <linearGradient
            id="paint2_linear_1154_9948"
            x1="160.939"
            y1="38"
            x2="387.87"
            y2="302.883"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0.25" stopColor="white" stopOpacity="0" />
            <stop offset="0.46" stopColor="white" />
            <stop offset="0.78" stopColor="white" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
      <svg viewBox="0 0 199 183" fill="none" className={styles.diamond}>
        <DiamondAnimation optimized />
      </svg>
      <div className={styles.title}>Collected items</div>
      <Swiper
        onSwiper={(swiperInstance) => {
          swiperRef.current = swiperInstance
        }}
        pagination={pagination}
        grabCursor
        centeredSlides
        loop
        slidesPerView="auto"
        modules={[Pagination]}
        className={styles.swiper}
      >
        {pagedItems.map((page, i) => (
          <SwiperSlide className={styles.slide} key={i}>
            <div className={styles.things}>
              {page.map((card) => {
                const isAvatar =
                  card.displayType &&
                  card.displayType.toLowerCase().includes('avatar')
                const isCard =
                  card.displayType &&
                  card.displayType.toLowerCase().includes('card')
                return (
                  <div className={styles.thing} key={card.id}>
                    <InventoryThing
                      className={cn(styles.thingInner, {
                        [styles.avatarMask]: isAvatar || isCard,
                      })}
                      card={card}
                    />
                  </div>
                )
              })}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
      <div className={styles.arrows}>
        <div
          className={cn(styles.arrow, styles.arrowLeft)}
          onClick={() => {
            swiperRef.current?.slidePrev()
          }}
        >
          <SpiralSelectorArrowIcon />
        </div>
        <div
          className={cn(styles.arrow, styles.arrowRight)}
          onClick={() => {
            swiperRef.current?.slideNext()
          }}
        >
          <SpiralSelectorArrowIcon />
        </div>
      </div>
    </div>
  )
}
